import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TrendDownIcon } from '../../../../assets/icons/dashboard/decrease-arrow-icon.svg';
import { ReactComponent as TrendUpIcon } from '../../../../assets/icons/dashboard/increse-arrow-icon.svg';
import { ReactComponent as RevenueIcon } from '../../../../assets/icons/dashboard/revenue-status-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as EnergyIcon } from '../../../../assets/icons/dashboard/white-energy.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
import { ReactComponent as MultiUsersIcon } from '../../../../assets/icons/sidebar/sidebar-account-active.svg';
import { ReactComponent as ActiveDevicesIcon } from '../../../../assets/icons/sidebar/sidebar-device-active.svg';
import MyButton from '../../../../shared-components/button/Button';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

// Define the type for card data

interface CardData {
  count: string | number;
  title: string;
  subTitle?: string;
  icon: React.ReactNode;
  trend: number;
  trendType: 'up' | 'down';
  iconBgColor: string;
  trendBgColor: string;
  trendColor: string;
  isLoading: boolean;
}

interface DashboardCardsProperties {
  startDate: string;
  endDate: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Dashboard cards component with background scrolling image
 * @param {DashboardCardsProperties} properties - Component properties
 * @returns {React.JSX.Element} Dashboard cards component
 */
function DashboardCards(properties: DashboardCardsProperties) {
  const { startDate, endDate, filters } = properties;
  const { t } = useTranslation('dashboard');
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [cardsState, setCardsState] = useState<{
    revenue: CardData;
    energy: CardData;
    activeDevices: CardData;
    // endConsumers: CardData;
    totalPartners: CardData;
    totalStation: CardData;
  }>({
    revenue: {
      count: 0,
      title: 'Total Revenue',
      subTitle: 'SAR',
      icon: <RevenueIcon />,
      trend: 12.5,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    energy: {
      count: 0,
      title: 'Total Energy',
      subTitle: 'KWh',
      icon: <EnergyIcon />,
      trend: 8.2,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    activeDevices: {
      count: 0,
      title: 'Active Devices',
      icon: <ActiveDevicesIcon />,
      trend: 4.1,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    // endConsumers: {
    //   count: 0,
    //   title: 'Total End Consumers',
    //   icon: <MultiUsersIcon />,
    //   trend: 0.5,
    //   trendType: 'down',
    //   iconBgColor: '#E0F2F1',
    //   trendBgColor: '#FFEBEE',
    //   trendColor: '#C62828',
    //   isLoading: true,
    // },
    totalPartners: {
      count: 0,
      title: 'Total Partners',
      icon: <MultiUsersIcon />,
      trend: 4.1,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
    totalStation: {
      count: 0,
      title: 'Total Stations',
      icon: <ActiveDevicesIcon />,
      trend: 4.1,
      trendType: 'up',
      iconBgColor: '#E0F2F1',
      trendBgColor: '#E0F2F1',
      trendColor: '#00695C',
      isLoading: true,
    },
  });

  /**
   * Fetches data for dashboard cards
   * @param {string} rangeStartDate - The range start date
   * @param {string} rangeEndDate - The range end date
   * @param {boolean} hardRefresh - The hardRefresh flag
   */
  const fetchCardData = async (
    rangeStartDate: string,
    rangeEndDate: string,
    hardRefresh = true,
  ) => {
    console.log('filters', filters);
    try {
      if (hardRefresh) setIsLoading(true);
      const [revenueResponse] = await Promise.allSettled([
        dashboard.fetchDashboardCardDetails({
          startDate: rangeStartDate,
          endDate: rangeEndDate,
          type: filters?.type,
          accountCustomId: filters?.facilityIds,
          stationIds: filters?.stationIds,
          deviceIds: filters?.deviceIds,
          hardRefresh,
        }),
      ]);

      setCardsState((previousState) => {
        const newState = { ...previousState };
        if (revenueResponse.status === 'fulfilled' && revenueResponse.value.status === 200) {
          const finalResult = revenueResponse?.value?.data?.finalResult;
          const countData = finalResult?.count;
          const energy = finalResult?.electricity?.total || 0;

          newState.revenue.count = finalResult?.revenue?.total || 0;
          newState.revenue.subTitle = finalResult?.revenue?.currency || '';
          newState.energy.count = (energy / 1000).toFixed(2) || 0;
          newState.activeDevices.count = finalResult?.electricity?.activeDevices || 0;
          newState.totalPartners.count = countData?.totalPartner ?? 0;
          newState.totalStation.count = countData?.totalStation ?? 0;

          setLastUpdatedTime(
            revenueResponse?.value?.data?.last_updated_time || new Date().toString(),
          );
        }
        newState.revenue.isLoading = false;
        newState.energy.isLoading = false;
        newState.activeDevices.isLoading = false;
        newState.totalPartners.isLoading = false;
        newState.totalStation.isLoading = false;

        return newState;
      });
    } catch (error) {
      console.error('Error fetching dashboard cards data', error);
      setCardsState((previousState) => ({
        ...previousState,
        revenue: { ...previousState.revenue, isLoading: false },
        energy: { ...previousState.energy, isLoading: false },
        activeDevices: { ...previousState.activeDevices, isLoading: false },
        totalPartners: { ...previousState.totalPartners, isLoading: false },
        totalStation: { ...previousState.totalStation, isLoading: false },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Manually refreshes the dashboard cards data
   */
  const handleManualRefresh = () => {
    setCardsState((previous) => ({
      revenue: { ...previous.revenue, isLoading: true },
      energy: { ...previous.energy, isLoading: true },
      activeDevices: { ...previous.activeDevices, isLoading: true },
      totalPartners: { ...previous.totalPartners, isLoading: true },
      totalStation: { ...previous.totalStation, isLoading: true },
    }));
    fetchCardData(startDate, endDate, true);
  };

  useEffect(() => {
    fetchCardData(startDate, endDate, false);
  }, [startDate, endDate, filters]);

  /**
   * Renders a single dashboard card
   * @param {CardData} card - The card data to render
   * @returns {React.JSX.Element} Single card node
   */
  const renderCard = (card: CardData) => (
    <div className="new-dashboard-card">
      <div className="top-section">
        <h1 className="count">
          {card.isLoading
            ? '...'
            : Number(card.count ?? 0).toLocaleString()}{' '}
          {card.subTitle && <span className="subtitle">{card.subTitle}</span>}
        </h1>
        <div className="icon-wrapper">{card.icon}</div>
      </div>
      <div className="bottom-section">
        <p className="title">{card.title}</p>
        <div
          className="trend-wrapper"
          style={{ backgroundColor: card.trendBgColor, display: 'none' }}>
          {card.trendType === 'up' ? <TrendUpIcon /> : <TrendDownIcon />}
          <span style={{ color: card.trendColor }}>{card.trend}%</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-cards-outer">
      <div className="refresh-section">
        <div className="refresh-icon">
          {isLoading ? (
            <div className="loading">
              <LoaderIcon style={{ width: '20px', height: '20px' }} />
            </div>
          ) : (
            <MyButton
              label=""
              buttonType="none"
              onClickFunc={handleManualRefresh}
              svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
            />
          )}
        </div>
        <p className="updated-time">
          {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}{' '}
        </p>
      </div>
      <div className="dashboard-cards-wrap-new">
        {renderCard(cardsState.totalPartners)}
        {renderCard(cardsState.totalStation)}
        {renderCard(cardsState.activeDevices)}
        {renderCard(cardsState.energy)}
        {renderCard(cardsState.revenue)}
        {/* {renderCard(cardsState.endConsumers)} */}
      </div>
    </div>
  );
}

DashboardCards.defaultProps = {
  filters: undefined,
};

export default DashboardCards;
